import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { ArrowLeft, Dumbbell, Clock, Repeat, Timer, Loader2 } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WorkoutPlan = () => {
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const userId = localStorage.getItem('userId');
        if (!userId) {
          toast.error('Bitte absolviere zuerst das Quiz');
          navigate('/quiz');
          return;
        }

        const response = await axios.get(`${BACKEND_URL}/api/workout/plan/${userId}`);
        setPlan(response.data.plan);
      } catch (error) {
        console.error('Error fetching workout plan:', error);
        if (error.response?.status === 404) {
          toast.error('Kein Trainingsplan gefunden. Bitte absolviere zuerst das Quiz.');
          navigate('/quiz');
        } else {
          toast.error('Fehler beim Laden des Plans');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPlan();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-purple-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            <Button
              data-testid="back-to-dashboard-btn"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="h-10 px-4 rounded-lg hover:bg-zinc-800"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Dashboard
            </Button>
            <h1 className="text-xl font-bold">Trainingsplan</h1>
            <div className="w-24" />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 md:px-8 max-w-5xl py-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 rounded-xl bg-purple-500/10 flex items-center justify-center text-purple-400">
              <Dumbbell className="w-7 h-7" />
            </div>
            <div>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">Dein Trainingsplan</h2>
              <p className="text-base text-zinc-400 mt-1">Personalisiert für deine Ziele</p>
            </div>
          </div>
        </motion.div>

        {/* Tips */}
        {plan?.tips && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8 bg-purple-500/10 border border-purple-500/20 rounded-xl p-6"
          >
            <h3 className="text-lg font-semibold text-purple-400 mb-3">Tipps für optimale Ergebnisse</h3>
            <ul className="space-y-2">
              {plan.tips.map((tip, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-zinc-300">
                  <span className="text-purple-400 mt-1">•</span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        )}

        {/* Weekly Plan */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {plan?.week_plan?.map((day, index) => (
              <AccordionItem
                key={index}
                value={`day-${day.day}`}
                className="bg-zinc-900/50 backdrop-blur-md border border-zinc-800/50 rounded-2xl overflow-hidden"
              >
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-zinc-800/30">
                  <div className="flex items-center justify-between w-full pr-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center text-purple-400 font-bold">
                        {day.day}
                      </div>
                      <div className="text-left">
                        <div className="text-lg font-semibold">{day.day_name}</div>
                        <div className="text-sm text-zinc-400">{day.focus}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-zinc-400">
                      <Clock className="w-4 h-4" />
                      {day.duration}
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-4 mt-4">
                    {day.exercises?.map((exercise, exIndex) => (
                      <div
                        key={exIndex}
                        className="bg-zinc-800/30 rounded-xl p-4 flex items-center justify-between"
                      >
                        <div className="flex-1">
                          <h4 className="font-semibold text-white mb-2">{exercise.name}</h4>
                          <div className="flex items-center gap-4 text-sm text-zinc-400">
                            <div className="flex items-center gap-1">
                              <Repeat className="w-4 h-4" />
                              <span>{exercise.sets} Sets</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Dumbbell className="w-4 h-4" />
                              <span>{exercise.reps} Reps</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Timer className="w-4 h-4" />
                              <span>{exercise.rest} Pause</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </div>
  );
};

export default WorkoutPlan;